import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert, Card, CardContent, Divider, Link, InputAdornment, IconButton, Stepper, Step, StepLabel, LinearProgress
} from '@mui/material';
import { Email as EmailIcon, VpnKey as VpnKeyIcon, ArrowBack as ArrowBackIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Custom MUI Theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', dark: '#1565c0' },
    secondary: { main: '#8E24AA' },
    success: { main: '#4CAF50' },
    background: { paper: '#FFFFFF', default: '#F5F7FA' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    body2: { fontWeight: 400, lineHeight: 1.6 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '10px 20px', // Slightly smaller padding
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transform: 'translateY(-1px)' },
          fontSize: '0.9rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#F9FAFB',
            '&:hover fieldset': { borderColor: '#1976d2' },
            '&.Mui-focused fieldset': { borderWidth: 2, borderColor: '#1976d2' },
          },
          '& .MuiInputLabel-root': { fontWeight: 500, color: '#6B7280', fontSize: '0.9rem' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
        },
      },
    },
  },
});

// Password strength checker
const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 6) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^A-Za-z0-9]/.test(password)) strength += 1;
  return strength;
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { requestPasswordResetOtp, verifyOtpAndResetPassword } = useAuth();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await requestPasswordResetOtp(email);
      if (result.success) {
        setSuccess(result.message);
        setStep(2);
        setCountdown(60);
      } else {
        setError(result.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError('A network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await verifyOtpAndResetPassword(email, otp, newPassword);
      if (result.success) {
        setSuccess(result.message);
        setStep(3);
      } else {
        setError(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('A network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    await handleSendOtp();
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabel = ['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Weak';
  const strengthColor = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6'][passwordStrength - 1] || '#EF4444';

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
          p: { xs: 2, sm: 3 }, // Reduced padding for smaller screens
          position: 'relative',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Card
            sx={{
              maxWidth: 420, // Consistent width
              width: '100%',
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              borderRadius: 4,
              bgcolor: 'background.paper',
              background: 'linear-gradient(180deg, #FFFFFF, #F9FAFB)', // Subtle card gradient
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}> {/* Reduced padding */}
              <Box sx={{ mb: 2 }}> {/* Reduced margin */}
                <Stepper activeStep={step - 1} sx={{ mb: 2, '& .MuiStepLabel-root': { fontSize: '0.85rem' } }}>
                  <Step>
                    <StepLabel>Enter Email</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Verify OTP</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Success</StepLabel>
                  </Step>
                </Stepper>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    color="primary.main"
                    sx={{ fontSize: { xs: '1.6rem', sm: '1.9rem' }, mb: 1 }}
                  >
                    Reset Your Password
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 360, mx: 'auto', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                  >
                    {step === 1
                      ? 'Enter your email to receive a secure OTP code.'
                      : step === 2
                        ? 'Enter the OTP and set a new password.'
                        : 'Your password has been successfully reset!'}
                  </Typography>
                </Box>
              </Box>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Alert severity="error" sx={{ mb: 1.5, borderRadius: 8, fontSize: '0.85rem' }}>{error}</Alert>
                  </motion.div>
                )}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Alert severity="success" sx={{ mb: 1.5, borderRadius: 8, fontSize: '0.85rem' }}>{success}</Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TextField
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      aria-describedby="email-helper-text"
                    />
                    <Typography variant="body2" color="text.secondary" mb={2} id="email-helper-text" sx={{ fontSize: '0.85rem' }}>
                      We'll send a 6-digit code to reset your password.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSendOtp}
                      disabled={loading || !email}
                      sx={{ mb: 1 }}
                      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VpnKeyIcon />}
                      aria-label="Send OTP code"
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP Code'}
                    </Button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setStep(1)}
                        sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem', p: '4px 8px', minWidth: 0 }}
                        aria-label="Go back to email input"
                      >
                        Back
                      </Button>
                      <Button
                        variant="text"
                        onClick={handleResendOtp}
                        disabled={countdown > 0}
                        sx={{
                          color: countdown > 0 ? 'text.disabled' : 'primary.main',
                          fontSize: '0.85rem',
                          p: '4px 8px',
                          '&:hover': { bgcolor: 'action.hover', borderRadius: 4 },
                        }}
                        aria-label="Resend OTP"
                      >
                        {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                      </Button>
                    </Box>
                    <TextField
                      label="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      inputProps={{
                        maxLength: 6,
                        style: { textAlign: 'center', fontSize: '1.1rem', letterSpacing: '0.3em' },
                      }}
                      placeholder="000000"
                      aria-describedby="otp-helper-text"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" mb={2} id="otp-helper-text" sx={{ fontSize: '0.85rem' }}>
                      Enter the 6-digit code sent to your email.
                    </Typography>
                    <TextField
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {newPassword && (
                      <Box sx={{ mb: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(passwordStrength / 4) * 100}
                          sx={{
                            height: 4, // Thinner progress bar
                            borderRadius: 4,
                            bgcolor: 'grey.300',
                            '& .MuiLinearProgress-bar': { bgcolor: strengthColor },
                          }}
                        />
                        <Typography variant="caption" color={strengthColor} sx={{ mt: 0.3, fontWeight: 500, fontSize: '0.75rem' }}>
                          Strength: {strengthLabel}
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleResetPassword}
                      disabled={loading || !otp || !newPassword || !confirmPassword}
                      sx={{ mb: 1 }}
                      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VpnKeyIcon />}
                      aria-label="Reset password"
                    >
                      {loading ? 'Resetting Password...' : 'Reset Password'}
                    </Button>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.4, ease: 'backOut' }}
                      >
                        <Typography
                          variant="h6"
                          color="success.main"
                          mb={1.5}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '1.2rem' }}
                        >
                          All Set!
                        </Typography>
                      </motion.div>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={2}
                        sx={{ maxWidth: 360, mx: 'auto', fontSize: '0.85rem' }}
                      >
                        Your password has been successfully reset. You can now log in with your new password.
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleGoToLogin}
                        sx={{ mb: 1 }}
                        startIcon={<VpnKeyIcon />}
                        aria-label="Go to login page"
                      >
                        Go to Login
                      </Button>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

              <Divider sx={{ my: 2, bgcolor: 'grey.300' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  Remember your password?{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleGoToLogin}
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 600,
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline', color: 'primary.dark' },
                    }}
                    aria-label="Sign in"
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Box>
    </ThemeProvider>
  );
};

export default ForgotPasswordPage;